import {
  assertCanonicalString,
  concatBytes,
  getSubtleCrypto,
  lengthPrefixedUtf8,
  sha256,
  u8,
  u32be,
  utf8
} from "./encoding";
import { fail } from "./errors";
import { normalizeSeed } from "./seed";
import type { DealConfigInput, NormalizedDealConfig } from "./types";

const CONFIG_MAGIC = utf8("YSI-DEAL-CONFIG-V1\0");
const U32_MAX = 0xffff_ffff;

export function normalizeDealConfig(
  input: DealConfigInput
): NormalizedDealConfig {
  assertCanonicalString(input.rulesVersion, "rulesVersion");
  assertCanonicalString(input.dealVersion, "dealVersion");
  assertCanonicalString(input.matcherVersion, "matcherVersion");
  assertCanonicalString(input.catalogRevision, "catalogRevision");

  if (input.dealVersion !== "deal-v1") {
    fail(
      "NON_CANONICAL_STRING",
      `Unsupported dealVersion: ${input.dealVersion}`,
      "dealVersion"
    );
  }
  if (![3, 4, 5].includes(input.playerCount)) {
    fail(
      "INTEGER_OUT_OF_RANGE",
      `playerCount must be 3, 4, or 5; received ${String(input.playerCount)}`,
      "playerCount"
    );
  }
  if (
    !Number.isInteger(input.round) ||
    input.round < 1 ||
    input.round > U32_MAX
  ) {
    fail(
      "INTEGER_OUT_OF_RANGE",
      `round must be in 1..${String(U32_MAX)}`,
      "round"
    );
  }

  return {
    rulesVersion: input.rulesVersion,
    dealVersion: "deal-v1",
    matcherVersion: input.matcherVersion,
    catalogRevision: input.catalogRevision,
    normalizedSeed: normalizeSeed(input.rawSeed),
    playerCount: input.playerCount,
    round: input.round
  };
}

export function encodeCanonicalConfig(
  config: NormalizedDealConfig
): Uint8Array {
  return concatBytes(
    CONFIG_MAGIC,
    lengthPrefixedUtf8(config.rulesVersion),
    lengthPrefixedUtf8(config.dealVersion),
    lengthPrefixedUtf8(config.matcherVersion),
    lengthPrefixedUtf8(config.catalogRevision),
    lengthPrefixedUtf8(config.normalizedSeed),
    u8(config.playerCount),
    u32be(config.round)
  );
}

export async function computeMasterDigest(
  config: NormalizedDealConfig,
  subtle: SubtleCrypto = getSubtleCrypto()
): Promise<Uint8Array> {
  return sha256(encodeCanonicalConfig(config), subtle);
}
